import {RootState} from '../State';
import {KeyedAction} from '../Store';
import {UserWorkspaceMeta, UpdatableWorkspaceMeta} from '../../controller/Workspaces';
import {WorkspaceStatusStates} from '../../types/enums';

export interface WorkspaceStatus {
    [id: string]: WorkspaceStatusStates;
}

export type WorkspacesState = Readonly<{
    workspaces: {[key: string]: UserWorkspaceMeta};
    statusById: WorkspaceStatus;
}>;

export const WorkspacesStateInitial: WorkspacesState = {
    workspaces: {},
    statusById: {}
};

export class RestoreWorkspace extends KeyedAction<RootState, 'workspaces'> {
    public readonly type: string = '@@WORKSPACE/RESTORE_WORKSPACE';

    constructor(public readonly id: string) {
        super('workspaces');
    }

    public reduce(state: WorkspacesState): WorkspacesState {
        const statusById = {...state.statusById, [this.id]: WorkspaceStatusStates.Loading};
        return {...state, statusById};
    }
}

/**
 * Updates a single workspace in the state and will push changes to the server.
 */
export class UpdateWorkspace extends KeyedAction<RootState, 'workspaces'> {
    public readonly type: string = '@@WORKSPACE/UPDATE_WORKSPACE';

    constructor(public readonly id: string, public readonly workspaceMeta: UpdatableWorkspaceMeta, public readonly shouldUpdateSnapshot: boolean) {
        super('workspaces');
    }

    public reduce(state: WorkspacesState): WorkspacesState {
        const newState = {...state};
        const meta = {...newState.workspaces[this.id], ...this.workspaceMeta};
        newState.workspaces[this.id] = meta;

        return newState;
    }
}

export class CreateWorkspace extends KeyedAction<RootState, 'workspaces'> implements UserWorkspaceMeta {
    public readonly type: string = '@@WORKSPACE/CREATE_WORKSPACE';

    public tags: string[];
    public description: string;

    constructor(public readonly id: string, public readonly name: string) {
        super('workspaces');

        // Once UI is defined, these will be made into public getter params
        this.tags = ['Clients', 'Buy', 'Equity', 'Trading', 'Research', 'Market'].filter(() => Math.random() > 0.5);
        this.description = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Donec non massa odio. Maecenas \
non mi non mi volutpat porta vel at elit. Suspendisse mattis ut massa in molestie. Quisque quis bibendum nunc, et \
ornare tellus. Morbi non neque id mi fringilla varius non non urna. Ut porttitor maximus vehicula.';
    }

    public reduce(state: WorkspacesState): WorkspacesState {
        const {id, name, tags, description} = this;
        const newState = {...state};

        newState.workspaces[this.id] = {id, name, tags, description};
        newState.statusById[this.id] = WorkspaceStatusStates.None;

        return newState;
    }
}

/**
 * Perform a bulk update of the workspaces in the store - for example, after login.
 */
export class PostWorkspacesFetch extends KeyedAction<RootState, 'workspaces'> {
    public readonly type: string = '@@WORKSPACE/POST_WORKSPACES_FETCH';

    constructor(private readonly workspaces: UserWorkspaceMeta[]) {
        super('workspaces');
    }

    public reduce(state: WorkspacesState): WorkspacesState {
        const workspaces = {};
        const statusById = {};

        this.workspaces.forEach((workspace) => {
            workspaces[workspace.id] = workspace;
            statusById[workspace.id] = WorkspaceStatusStates.None;
        });

        /*
          Note to maintainer: this action is designed to replace the state of workspaces completely
          (e.g. when the client fetches the complete workspace list from the server post login.)
          combining the new state with previous state will result in duplicate workspace entries.
        */
        return {workspaces: {...workspaces}, statusById: {...statusById}};
    }
}

export class DeleteWorkspace extends KeyedAction<RootState, 'workspaces'> {
    public readonly type: string = '@@WORKSPACE/DELETE_WORKSPACE';

    constructor(public readonly id: string) {
        super('workspaces');
    }

    public reduce(state: WorkspacesState): WorkspacesState {
        const {id} = this;

        if (state.workspaces.hasOwnProperty(id)) {
            const newState = {...state};
            delete newState.workspaces[id];

            if (state.statusById.hasOwnProperty(id)) {
                delete newState.statusById[id];
            }

            return newState;
        } else {
            return state;
        }
    }
}

/**
 * Perform an update of the status of a workspace
 */
export class UpdateWorkspaceStatus extends KeyedAction<RootState, 'workspaces'> {
    public readonly type: string = '@@WORKSPACE/UPDATE_WORKSPACE_STATUS';

    constructor(private readonly id: string, private readonly status: WorkspaceStatusStates) {
        super('workspaces');
    }

    public reduce(state: WorkspacesState): WorkspacesState {
        const statusById = {...state.statusById};
        statusById[this.id] = this.status;

        return {...state, statusById};
    }
}

export const getWorkspaceStatuses = (state: RootState): WorkspaceStatus => state.workspaces.statusById;
